/**
 * MCP ツール利用量 dashboard panel.
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体 / チーム別 / 個人別の MCP サーバー呼び出し回数・エラー率・実行時間を 1 画面に描画する presentation component。fetch は親が行い data を渡す
 * @graph-connects none
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import type {
  McpUsageGranularity,
  McpUsageServerBreakdownResponse,
  McpUsageSummaryResponse,
  McpUsageTimeseriesResponse,
  McpUsageToolBreakdownResponse,
  McpUsageUserBreakdownResponse,
} from '../../../types/api.js';

export interface McpUsagePanelProps {
  /** ステータステキスト用のコンテキストラベル (例: "全体", "Aチーム", "yamada@air-closet.com") */
  scopeLabel: string;
  summary: McpUsageSummaryResponse | undefined;
  isSummaryLoading: boolean;
  timeseries: McpUsageTimeseriesResponse | undefined;
  byServer: McpUsageServerBreakdownResponse | undefined;
  byTool: McpUsageToolBreakdownResponse | undefined;
  byUser: McpUsageUserBreakdownResponse | undefined;
  /** スコープが解決できない場合のメッセージ (例: "チームを選択してください") */
  emptyMessage?: string | null;
  /** timeseries 集計粒度 (UI セレクタの現在値) */
  granularity: McpUsageGranularity;
  /** granularity 変更時のコールバック */
  onGranularityChange: (value: McpUsageGranularity) => void;
}

/** @graph-connects none */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

/** @graph-connects none */
function formatRate(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

/** @graph-connects none */
function formatMs(value: number): string {
  return `${Math.round(value).toLocaleString()} ms`;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage dashboard panel (presentation のみ)
 * @graph-connects none
 */
export function McpUsagePanel({
  scopeLabel,
  summary,
  isSummaryLoading,
  timeseries,
  byServer,
  byTool,
  byUser,
  emptyMessage,
  granularity,
  onGranularityChange,
}: McpUsagePanelProps) {
  const { t } = useTranslation();

  if (emptyMessage) {
    return (
      <Card>
        <CardContent>
          <p className="text-center py-8 text-[var(--color-text-muted)]">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  if (isSummaryLoading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasCalls = (summary?.totalCalls ?? 0) > 0;

  const metrics = [
    { label: t('mcpUsage.metrics.totalCalls'), value: formatNumber(summary?.totalCalls ?? 0) },
    { label: t('mcpUsage.metrics.uniqueUsers'), value: formatNumber(summary?.uniqueUsers ?? 0) },
    {
      label: t('mcpUsage.metrics.uniqueServers'),
      value: formatNumber(summary?.uniqueServers ?? 0),
    },
    { label: t('mcpUsage.metrics.uniqueTools'), value: formatNumber(summary?.uniqueTools ?? 0) },
    {
      label: t('mcpUsage.metrics.errorRate'),
      value: hasCalls ? formatRate(summary?.errorRate ?? 0) : '—',
    },
    {
      label: t('mcpUsage.metrics.avgDurationMs'),
      value: hasCalls ? formatMs(summary?.avgDurationMs ?? 0) : '—',
    },
    {
      label: t('mcpUsage.metrics.estimatedTokensTotal'),
      value: formatNumber(summary?.estimatedTokensTotal ?? 0),
    },
  ];

  const timeseriesData = (timeseries?.data ?? []).map((point) => ({
    bucket: point.bucket,
    callCount: point.callCount,
    errorCount: point.errorCount,
  }));

  const serverRows = byServer?.data ?? [];
  const toolRows = byTool?.data ?? [];
  const userRows = byUser?.data ?? [];

  return (
    <div className="space-y-6">
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('mcpUsage.scopeLabelPrefix')}: {scopeLabel}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {metrics.map((metric) => (
          <Card key={metric.label}>
            <CardContent className="flex flex-col gap-1 px-4 py-3">
              <span className="text-xs text-[var(--color-text-muted)]">{metric.label}</span>
              <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                {metric.value}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('mcpUsage.timeseries.title')}
            </h2>
            <div
              className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]"
              role="group"
              aria-label={t('mcpUsage.granularity.label')}
            >
              {(['day', 'week', 'month'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    onGranularityChange(value);
                  }}
                  aria-pressed={granularity === value}
                  className={`px-2 py-1 rounded-md border transition-colors ${
                    granularity === value
                      ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)] bg-[var(--color-accent-muted)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                  }`}
                >
                  {t(`mcpUsage.granularity.${value}`)}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timeseriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="bucket" stroke="var(--color-text-muted)" />
              <YAxis stroke="var(--color-text-muted)" />
              <Tooltip
                wrapperStyle={{ zIndex: 50, outline: 'none' }}
                formatter={(value) =>
                  typeof value === 'number' ? formatNumber(value) : String(value ?? '')
                }
                contentStyle={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  borderColor: 'var(--color-border)',
                  borderRadius: '8px',
                  boxShadow: 'var(--shadow-lg)',
                }}
              />
              <Legend />
              <Bar
                dataKey="callCount"
                name={t('mcpUsage.metrics.totalCalls')}
                stackId="calls"
                fill="#6366f1"
              />
              <Bar
                dataKey="errorCount"
                name={t('mcpUsage.metrics.errorCount')}
                stackId="calls"
                fill="#ef4444"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('mcpUsage.breakdown.byServer')}
            </h2>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                    <th className="py-2 pr-4">{t('mcpUsage.breakdown.serverName')}</th>
                    <th className="py-2 pr-4 text-right">{t('mcpUsage.metrics.totalCalls')}</th>
                    <th className="py-2 pr-4 text-right">{t('mcpUsage.metrics.errorCount')}</th>
                    <th className="py-2 text-right">{t('mcpUsage.metrics.avgDurationMs')}</th>
                  </tr>
                </thead>
                <tbody>
                  {serverRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-[var(--color-text-muted)]">
                        {t('mcpUsage.empty')}
                      </td>
                    </tr>
                  ) : (
                    serverRows.map((row) => (
                      <tr key={row.serverName} className="border-b border-[var(--color-border)]">
                        <td className="py-2 pr-4 text-[var(--color-text-primary)]">
                          {row.serverName}
                        </td>
                        <td className="py-2 pr-4 text-right text-[var(--color-text-primary)]">
                          {formatNumber(row.callCount)}
                        </td>
                        <td className="py-2 pr-4 text-right text-[var(--color-text-secondary)]">
                          {formatNumber(row.errorCount)}
                        </td>
                        <td className="py-2 text-right text-[var(--color-text-secondary)]">
                          {formatMs(row.avgDurationMs)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('mcpUsage.breakdown.byTool')}
            </h2>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                    <th className="py-2 pr-4">{t('mcpUsage.breakdown.serverName')}</th>
                    <th className="py-2 pr-4">{t('mcpUsage.breakdown.toolName')}</th>
                    <th className="py-2 pr-4 text-right">{t('mcpUsage.metrics.totalCalls')}</th>
                    <th className="py-2 text-right">{t('mcpUsage.metrics.avgDurationMs')}</th>
                  </tr>
                </thead>
                <tbody>
                  {toolRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-[var(--color-text-muted)]">
                        {t('mcpUsage.empty')}
                      </td>
                    </tr>
                  ) : (
                    toolRows.map((row) => (
                      <tr
                        key={`${row.serverName}\t${row.toolName}`}
                        className="border-b border-[var(--color-border)]"
                      >
                        <td className="py-2 pr-4 text-[var(--color-text-secondary)]">
                          {row.serverName}
                        </td>
                        <td className="py-2 pr-4 text-[var(--color-text-primary)]">
                          {row.toolName}
                        </td>
                        <td className="py-2 pr-4 text-right text-[var(--color-text-primary)]">
                          {formatNumber(row.callCount)}
                        </td>
                        <td className="py-2 text-right text-[var(--color-text-secondary)]">
                          {formatMs(row.avgDurationMs)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('mcpUsage.breakdown.byUser')}
          </h2>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="py-2 pr-4">{t('mcpUsage.breakdown.user')}</th>
                  <th className="py-2 pr-4">{t('mcpUsage.breakdown.team')}</th>
                  <th className="py-2 pr-4 text-right">{t('mcpUsage.metrics.totalCalls')}</th>
                  <th className="py-2 text-right">{t('mcpUsage.metrics.estimatedTokensTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {userRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-[var(--color-text-muted)]">
                      {t('mcpUsage.empty')}
                    </td>
                  </tr>
                ) : (
                  userRows.map((row, index) => (
                    <tr
                      key={`${row.email ?? '__unknown__'}-${String(index)}`}
                      className="border-b border-[var(--color-border)]"
                    >
                      <td className="py-2 pr-4 text-[var(--color-text-primary)]">
                        {row.nickname ?? t('mcpUsage.breakdown.unassigned')}
                        {row.email ? (
                          <span className="text-xs text-[var(--color-text-muted)] ml-2">
                            ({row.email})
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 text-[var(--color-text-secondary)]">
                        {row.teamName ?? t('mcpUsage.breakdown.unassigned')}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-primary)]">
                        {formatNumber(row.callCount)}
                      </td>
                      <td className="py-2 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.estimatedTokensTotal)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
